import { Link } from "@tanstack/react-router";
import { Check, ArrowRight, type LucideIcon } from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";

export type ServicePageData = {
  eyebrow: string;
  title: string;
  sub: string;
  intro: string;
  features: { icon: LucideIcon; title: string; description: string }[];
  process: { step: string; title: string; description: string }[];
  deliverables: string[];
  techStack?: string[];
  pricing: { name: string; price: string; period?: string; features: string[]; highlighted?: boolean }[];
  faqs: { q: string; a: string }[];
};

export function ServicePage({ data }: { data: ServicePageData }) {
  return (
    <PageShell>
      <PageHero eyebrow={data.eyebrow} title={data.title} sub={data.sub} />

      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{data.intro}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">What you get</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.features.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">Our process</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {data.process.map((p) => (
              <div key={p.step} className="rounded-2xl border border-border bg-card p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-primary">{p.step}</div>
                <h3 className="mt-3 text-lg font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Deliverables</h2>
            <ul className="mt-6 space-y-3">
              {data.deliverables.map((d) => (
                <li key={d} className="flex gap-3 text-sm">
                  <Check className="h-5 w-5 flex-shrink-0 text-primary" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          {data.techStack && (
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Tech stack</h2>
              <div className="mt-6 flex flex-wrap gap-2">
                {data.techStack.map((t) => (
                  <span key={t} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-center">Engagement packages</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {data.pricing.map((p) => (
              <div
                key={p.name}
                className={`rounded-2xl border p-6 ${
                  p.highlighted ? "border-primary bg-primary/5 shadow-glow" : "border-border bg-card"
                }`}
              >
                <h3 className="text-lg font-semibold">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{p.price}</span>
                  {p.period && <span className="text-sm text-muted-foreground">/ {p.period}</span>}
                </div>
                <ul className="mt-5 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm">
                      <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold ${
                    p.highlighted ? "bg-gradient-brand text-white shadow-glow" : "border border-border hover:border-primary/40"
                  }`}
                >
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">Frequently asked questions</h2>
          <div className="mt-8 space-y-4">
            {data.faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="text-base font-semibold">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </PageShell>
  );
}
